import React, { useContext, useEffect, useState } from "react";
import { Button } from "../../components/button/Button";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { Subscription, useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  checkSubscriptionName,
  getDeliveryType,
  getFileType,
  getReportOptions,
  getScheduleOptions,
  getSettledError,
  getSettledValue,
  getSubscriptionDetails,
  setCurrentGroupId,
  setCurrentQueryId,
  setCurrentQueryName,
  setSubscriptionId,
  submitSubscribeData,
  updateSubscriptionDetail,
} from "../../redux/subscribeSlice";
import Loading from "../../components/Loading";
import LoadingOverlay from "../../components/LoadingOverlay";
import { ModalContext } from "../../components/Modal";
import { showNotification } from "../../redux/notificationSlice";

interface DropdownOption<T = string> {
  value: T;
  label: string;
}

const deliveryOptions: DropdownOption<number>[] = [
  { value: 1, label: "Email" },
];
const frequencyDayOptions: DropdownOption<string>[] = Array.from(
  { length: 31 },
  (_, i) => {
    const day = i + 1;

    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";

    return {
      value: day.toString(),
      label: `${day}${suffix}`,
    };
  },
);

interface SubscriptionType {
  mode: string;
}

const SubscriptionModel: React.FC<SubscriptionType> = ({ mode }) => {
  const dispatch: AppDispatch = useDispatch();
  const {
    reportType,
    queryName = "",
    groupId = null,
    queryId = null,
    subscriptionId = null,
  }: any = useSelector((state: RootState) => state.subscribe);
  const { user = null } = useSelector((state: RootState) => state.user);
  const today = new Date().toISOString();
  const [subscriptionName, setSubscriptionName] = useState("");
  const [deliveryType, setDeliveryType] = useState(1);
  const [fileType, setFileType] = useState("xlsx");
  const [scheduleType, setScheduleType] = useState<"DL" | "SD">("DL");
  const [startDate, setStartDate] = useState(today);
  const [frequencyValue, setFrequencyValue] = useState("1");
  const [frequencyDay, setFrequencyDay] = useState("1");

  const [fileTypeOptions, setfileTypeOptions] = useState<any>([]);
  const [scheduleOData, setScheduleData] = useState<any>([]);
  const [reportData, setReportData] = useState<any>([]);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [prevReports, setPrevReports] = useState([]);
  const modalContext = useContext(ModalContext);
  const close = modalContext?.close ?? (() => {});

  // const [deliveryOptions, setDeliveryOptions] = useState([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!subscriptionName.trim()) {
      newErrors.subscriptionName = "Subscription name is required.";
    }

    if (scheduleType === "SD") {
      if (!startDate) newErrors.startDate = "Start date is required.";

      if (!frequencyValue || isNaN(+frequencyValue) || +frequencyValue <= 0) {
        newErrors.frequencyValue = "Frequency must be a valid number!";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const [loading, setLoading] = useState(false);

  const isValidArray = (val: any) => Array.isArray(val) && val.length > 0;

  const fetchStaticApis = async () => {
    try {
      const results = await Promise.allSettled([
        dispatch(getReportOptions(groupId)),
        // dispatch(getDeliveryType()),
      ]);

      const [reportOptionsRes] = results;

      const reportOptions = getSettledValue(reportOptionsRes)?.payload;

      if (!getSettledError(reportOptionsRes) && isValidArray(reportOptions)) {
        setReportData(reportOptions);
      }
    } catch (err) {
      console.error("Static API error:", err);
    }
  };

  const fetchRuntimeApis = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        dispatch(getFileType(queryId)),
        dispatch(getScheduleOptions(queryId)),
      ]);

      const [fileTypeRes, scheduleOptionsRes] = results;

      const fileTypes = getSettledValue(fileTypeRes)?.payload;
      const scheduleOptions = getSettledValue(scheduleOptionsRes)?.payload;

      if (!getSettledError(fileTypeRes) && isValidArray(fileTypes)) {
        setfileTypeOptions(fileTypes);
      }
      if (
        !getSettledError(scheduleOptionsRes) &&
        isValidArray(scheduleOptions)
      ) {
        setScheduleData(scheduleOptions);
      }
    } catch (err) {
      console.error("Runtime API error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionDetails = async () => {
    setLoading(true);
    let response = await dispatch(getSubscriptionDetails(subscriptionId));
    if (getSubscriptionDetails.fulfilled.match(response)) {
      dispatch(setSubscriptionId(response.payload?.subscrId || null));
      if (response.payload.reports?.length > 0) {
        setPrevReports(response.payload.reports);
        dispatch(setCurrentQueryId(response.payload.reports[0].id));
        dispatch(setCurrentQueryName(response.payload.reports[0].workbookName));
        setFileType(response.payload.reports[0].fileType);
      }
      if (response.payload.delivery) {
        setScheduleType(response.payload.delivery.deliveryType);
        setFrequencyDay(response.payload.delivery.freqDay || "1");
        setFrequencyValue(response.payload.delivery.freqMonth || "1");
        if (
          response.payload.delivery.startDate &&
          response.payload.delivery.startDate != ""
        ) {
          setStartDate(
            convertToISOFromTimeStamp(response.payload.delivery.startDate),
          );
        }
      }
      setDeliveryType(response.payload.deliveryMode);
      setSubscriptionName(response.payload.subscriptionName);
    } else {
      setLoading(false);
      dispatch(
        showNotification({
          message: "An unexpected error occurred!",
          duration: 3000,
          isError: true,
        }),
      );
    }
  };
  useEffect(() => {
    if (mode != "create" && subscriptionId && !queryId) {
      fetchSubscriptionDetails();
    } else {
      if (queryId) {
        fetchRuntimeApis();
        if (mode == "create") {
          fetchStaticApis();
        }
      }
    }
  }, [queryId]);

  const generatePayload: any = () => {
    const generatedPayload: any = {
      subscrId: mode == "create" ? null : subscriptionId,
      isEdit: mode == "edit",
      userIds: [user?.name || ""],
      subscriptionName: subscriptionName,
      subscriptionDescription: "",
      dataTypes: [scheduleOData[0].value],
      reportType: null,
      // reports: [
      //   {
      //     dbId: 0,
      //     id: queryId,
      //     type: null,
      //     mainType: reportType,
      //     workbookName: queryName,
      //     worksheetKey: queryId,
      //     worksheetName: reportData?.[0]?.value || "",
      //     worksheetDispName: reportData?.[0]?.value || "",
      //     fileType: fileType,
      //     reportType: reportType,
      //     reportFormat: null,
      //     subscrParams: null,
      //   },
      // ],

      delivery: {
        deliveryType: scheduleType,
        startDate: scheduleType === "DL" ? null : startDate,
        freqMonth: scheduleType === "DL" ? null : Number(frequencyValue),
        freqDay: scheduleType === "DL" ? null : Number(frequencyDay),
      },

      archive: 0,
      deliveryMode: deliveryType,
      saveOption: "SAVE",
      cloudProviderCode: null,
    };
    if (reportData.length > 0) {
      generatedPayload["reports"] = reportData.map((report: any) => {
        return {
          dbId: 0,
          id: (report.key && parseInt(report.key)) || queryId,
          type: null,
          mainType: reportType,
          workbookName: queryName,
          worksheetKey: queryId,
          worksheetName: report.value || "",
          worksheetDispName: report.value || "",
          fileType: fileType,
          reportType: reportType,
          reportFormat: null,
          subscrParams: null,
        };
      });
    } else if (prevReports.length > 0) {
      generatedPayload["reports"] = prevReports.map((report: any) => {
        return {
          dbId: 0,
          id: report.id || null,
          type: null,
          mainType: reportType,
          workbookName: queryName,
          worksheetKey: queryId,
          worksheetName: report.worksheetName || "",
          worksheetDispName: report.worksheetDispName || "",
          fileType: fileType,
          reportType: reportType,
          reportFormat: null,
          subscrParams: null,
        };
      });
    } else {
      generatedPayload["reports"] = [];
    }

    return generatedPayload;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = e.target.value;
    const isoString = new Date(dateValue).toISOString();
    setStartDate(isoString);
  };

  const convertToISOFromTimeStamp = (value: string | number) => {
    if (!value) return "";

    const date = new Date(Number(value));

    const pad = (n: number) => String(n).padStart(2, "0");

    const tzOffset = -date.getTimezoneOffset();
    const sign = tzOffset >= 0 ? "+" : "-";
    const hours = pad(Math.floor(Math.abs(tzOffset) / 60));
    const minutes = pad(Math.abs(tzOffset) % 60);

    return (
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
      `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
      `${sign}${hours}:${minutes}`
    );
  };

  const formatForInput = (isoString: string) => {
    if (!isoString) return "";
    return isoString.split("T")[0];
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = generatePayload();
    setSubmitLoading(true);

    try {
      if (mode == "create") {
        const nameCheckResult = await dispatch(
          checkSubscriptionName(payload.subscriptionName),
        );

        if (!checkSubscriptionName.fulfilled.match(nameCheckResult)) {
          throw new Error(
            nameCheckResult.error?.message ||
              "Failed to validate subscription name!",
          );
        }

        if (nameCheckResult.payload?.data != 0) {
          dispatch(
            showNotification({
              message: "The subscription name already exists!",
              duration: 3000,
              isError: true,
            }),
          );
          return;
        }
      }

      const submitResult = await dispatch(submitSubscribeData(payload));

      if (!submitSubscribeData.fulfilled.match(submitResult)) {
        throw new Error(
          submitResult.error?.message || "Failed to submit subscription!",
        );
      }
      dispatch(
        updateSubscriptionDetail({
          deliveryMode: deliveryOptions.filter(
            (item) => item.value == deliveryType,
          )[0].label,
          id: subscriptionId,
          subscriptionName: subscriptionName,
        }),
      );
      dispatch(setCurrentQueryId(null));
      dispatch(setCurrentGroupId(null));
      dispatch(setCurrentQueryName(""));
      dispatch(setSubscriptionId(null));

      dispatch(
        showNotification({
          message:
            mode == "edit"
              ? "Subscription updated successfully."
              : "Subscription created successfully.",
          duration: 3000,
          isError: false,
        }),
      );

      close();
    } catch (error: any) {
      console.error("Error:", error);

      dispatch(
        showNotification({
          message: error.message || "An unexpected error occurred",
          duration: 3000,
          isError: true,
        }),
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="dialog-container subscription-model">
      <LoadingOverlay show={submitLoading} />
      <div className="dialog-header">
        <div className="dialog-title">
          <h3>
            {mode == "create"
              ? "Setup Subscription"
              : mode == "edit"
                ? "Edit Subscription"
                : "Subscription Details"}
          </h3>
        </div>
      </div>
      {loading ? (
        <div
          style={{
            minWidth: 520,
            minHeight: 340,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {" "}
          <Loading show={loading} />{" "}
        </div>
      ) : (
        <div
          style={{
            padding: "15px 20px",
            display: "flex",
            minWidth: 520,
            // width:"100%",
            flexDirection: "column",
            gap: 10,
            overflow: "auto",
            flex: 1,
          }}
        >
          <FieldContainer label="Query Name">
            <input
              value={queryName}
              className="input-style"
              readOnly
              style={{
                backgroundColor: "#F0F0F0",
                cursor: "not-allowed",
              }}
              placeholder="Query Name"
            />
          </FieldContainer>

          <FieldContainer label="Subscription Name">
            <input
              className="input-style"
              maxLength={100}
              disabled={mode == "view"}
              placeholder="e.g., Monthly Sales Report"
              value={subscriptionName}
              onChange={(e) => setSubscriptionName(e.target.value)}
            />
            {errors.subscriptionName && (
              <span className="error-text">{errors.subscriptionName}</span>
            )}
          </FieldContainer>

          <div style={{ display: "flex", gap: 10 }}>
            <FieldContainer label="Delivery Type" flex>
              <Dropdown
                id="delivery-type"
                disabled={mode == "view"}
                selectedValue={deliveryType}
                onChange={(value: number) => setDeliveryType(value)}
                options={deliveryOptions}
              />
            </FieldContainer>

            <FieldContainer label="File Type" flex>
              <Dropdown
                id="file-type"
                selectedValue={fileType}
                disabled={mode == "view"}
                onChange={(value: string) => setFileType(value)}
                options={(fileTypeOptions ?? []).map((item: any) => ({
                  value: item,
                  label: item.toUpperCase(),
                }))}
              />
            </FieldContainer>
          </div>

          <div style={{ fontWeight: "bold" }}>Delivery Schedule</div>

          <ScheduleCard>
            <label className="custom-radio radio-row">
              <input
                type="radio"
                value="dateLoad"
                disabled={mode == "view"}
                checked={scheduleType === "DL"}
                onChange={() => setScheduleType("DL")}
              />
              <span className="checkmark" style={{ marginTop: 3 }}></span>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <label className="label-bold">Data Load</label>
                <label className="label-small">
                  {`Deliver when ${
                    scheduleOData[0]?.value?.toLowerCase() || ""
                  } data is loaded`}
                </label>
              </div>
            </label>
          </ScheduleCard>

          <ScheduleCard>
            <label className="custom-radio radio-row">
              <input
                type="radio"
                value="scheduled"
                disabled={mode == "view"}
                checked={scheduleType === "SD"}
                onChange={() => setScheduleType("SD")}
              />
              <span className="checkmark" style={{ marginTop: 3 }}></span>

              <div
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label className="label-bold">Scheduled Delivery</label>
                  <label className="label-small">
                    Set a specific delivery schedule
                  </label>
                </div>

                <FieldContainer label="Start Date">
                  <input
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={formatForInput(startDate)}
                    disabled={mode === "view" || scheduleType == "DL"}
                    onChange={handleDateChange}
                    style={inputStyle}
                  />

                  {errors.startDate && (
                    <span className="error-text">{errors.startDate}</span>
                  )}
                </FieldContainer>

                <FieldContainer label="Frequency">
                  <div
                    style={{ display: "flex", gap: 5, alignItems: "center" }}
                  >
                    <label style={{ fontSize: 14 }}>Deliver every</label>
                    <input
                      value={frequencyValue}
                      disabled={mode === "view" || scheduleType == "DL"}
                      type="number"
                      min={1}
                      max={12}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0 && value <= 12) {
                          setFrequencyValue(e.target.value);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                      style={{ ...inputStyle, width: 50 }}
                    />

                    <span style={{ fontSize: 14 }}>Month(s) on the</span>

                    <Dropdown
                      id="frequency-day"
                      disabled={mode == "view" || scheduleType == "DL"}
                      selectedValue={frequencyDay}
                      onChange={(value: string) => setFrequencyDay(value)}
                      options={frequencyDayOptions}
                    />
                  </div>

                  {errors.frequencyValue && (
                    <span className="error-text">{errors.frequencyValue}</span>
                  )}
                </FieldContainer>
              </div>
            </label>
          </ScheduleCard>
        </div>
      )}

      <div className="dialog-footer">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Button
            className="btn sm"
            color="primary"
            variant="outline"
            onClick={() => {
              dispatch(setCurrentQueryId(null));
              dispatch(setCurrentGroupId(null));
              dispatch(setCurrentQueryName(""));
              dispatch(setSubscriptionId(null));
              close();
            }}
            style={{ textTransform: "capitalize" }}
          >
            {mode == "view" ? "Close" : "Cancel"}
          </Button>
          {mode != "view" && (
            <Button
              className="btn sm"
              disabled={loading}
              color="primary"
              style={{ textTransform: "capitalize" }}
              onClick={handleSave}
            >
              {mode == "edit" ? "Save" : "Save"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModel;

interface FieldProps {
  label: string;
  children: React.ReactNode;
  flex?: boolean;
}

const FieldContainer: React.FC<FieldProps> = ({ label, children, flex }) => (
  <div
    style={{
      width: "100%",
      display: "flex",
      flexDirection: "column",
      flex: flex ? 1 : undefined,
    }}
  >
    <label className="label-bold">{label}</label>
    {children}
  </div>
);

const ScheduleCard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div style={{ border: "1px solid lightgray", padding: 10 }}>{children}</div>
);

const inputStyle: React.CSSProperties = {
  border: "1px solid #CCCCCC",
  fontSize: 14,
  padding: "5px 5px",
};

const errorStyle: React.CSSProperties = {
  color: "red",
  fontSize: 12,
  marginTop: 2,
};
